'use client';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@ui/icons';
import { ResponsiveImage } from '@web/molecules';
import { VisuallyHidden, SVGIcon } from '@web/atoms';
import { useReporter } from '../../reporter';
import {
  useExperiment,
  OLLIE_APP_FOOTER_DOWNLOAD_BUTTONS_KEY,
} from '../../experiments';
import {
  socialMediaIcons,
  OLLIE_APP_APP_STORE_LINKS,
  socialMediaActionText,
} from './constants';
import {
  StyledFooterColumn,
  BusinessHours,
  Content,
  ColumnTitle,
  ColumnToggle,
  ToggleIcon,
  ColumnContent,
  ColumnDivider,
  SocialIcon,
  OllieAppButtonsContainer,
  SocialIconsGrid,
  AppStoreIconsGrid,
} from './styles';
import { FooterLink } from './FooterLink';
import { footerLinkClicked } from './events';

export const FooterColumn = ({ links, businessHours, content, title }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <StyledFooterColumn>
      <ColumnDivider />
      <ColumnToggle
        onClick={() => setIsOpen(prev => !prev)}
        aria-expanded={isOpen}
      >
        <ColumnTitle as="span">{title}</ColumnTitle>
        <ToggleIcon $isOpen={isOpen}>
          <Icon
            iconName="plus"
            alt={isOpen ? 'Collapse' : 'Expand'}
            size={0.5}
            color="text.lightest"
            id={`footer-toggle-${title}`}
          />
        </ToggleIcon>
      </ColumnToggle>
      <ColumnTitle $desktopOnly>{title}</ColumnTitle>
      <ColumnContent $isOpen={isOpen}>
        {businessHours && <BusinessHours>{businessHours}</BusinessHours>}
        {content && <Content>{content}</Content>}
        {links.map(({ path, ...linkProps }, idx) => (
          <FooterLink
            {...linkProps}
            href={path}
            key={path}
            columnTitle={title}
            index={idx}
          />
        ))}
      </ColumnContent>
    </StyledFooterColumn>
  );
};

export const ConnectColumn = ({
  title,
  appStoreSectionTitle,
  socialIcons = [],
  appStoreIcons = [],
}) => {
  const reporter = useReporter();
  return (
    <StyledFooterColumn>
      <ColumnTitle $alwaysVisible>{title}</ColumnTitle>
      <SocialIconsGrid>
        {socialIcons.map(({ image, targetUrl, opensInNewTab }, i) => (
          <a
            key={i}
            href={targetUrl}
            target={opensInNewTab ? '_blank' : undefined}
            rel={opensInNewTab ? 'noreferrer' : undefined}
            onClick={() =>
              reporter.tag(footerLinkClicked(image.alt, targetUrl, title, i))
            }
          >
            <img src={image.src} alt={image.alt} />
          </a>
        ))}
      </SocialIconsGrid>
      {appStoreIcons.length > 0 && (
        <>
          {appStoreSectionTitle && (
            <ColumnTitle $alwaysVisible $sectionTitle>
              {appStoreSectionTitle}
            </ColumnTitle>
          )}
          <AppStoreIconsGrid>
            {appStoreIcons.map(({ image, targetUrl, opensInNewTab }, i) => (
              <a
                key={i}
                href={targetUrl}
                target={opensInNewTab ? '_blank' : undefined}
                rel={opensInNewTab ? 'noreferrer' : undefined}
                onClick={() =>
                  reporter.tag(
                    footerLinkClicked(image.alt, targetUrl, title, i),
                  )
                }
              >
                <img src={image.src} alt={image.alt} />
              </a>
            ))}
          </AppStoreIconsGrid>
        </>
      )}
    </StyledFooterColumn>
  );
};

export const SocialColumn = () => {
  const reporter = useReporter();
  const { value: showOllieAppDownloadButtons } = useExperiment(
    OLLIE_APP_FOOTER_DOWNLOAD_BUTTONS_KEY,
    false,
  );
  return (
    <StyledFooterColumn>
      <ColumnTitle>Social</ColumnTitle>
      {socialMediaIcons.map(({ src, alt, href }, i) => (
        <SocialIcon key={i} href={href} target="_blank">
          <SVGIcon
            icon={src}
            alt={alt}
            height={'20px'}
            width={'20px'}
            fillColor={'text.lightest'}
          />
          <VisuallyHidden>{socialMediaActionText}</VisuallyHidden>
        </SocialIcon>
      ))}
      {showOllieAppDownloadButtons && (
        <>
          <ColumnTitle>GET THE OLLIE APP</ColumnTitle>
          <OllieAppButtonsContainer>
            {OLLIE_APP_APP_STORE_LINKS.map(
              ({ src, alt, href, clickEvent }, i) => (
                <a
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => reporter.tag(clickEvent())}
                >
                  <ResponsiveImage
                    src={src}
                    alt={alt}
                    height={56}
                    width={189}
                  />
                </a>
              ),
            )}
          </OllieAppButtonsContainer>
        </>
      )}
    </StyledFooterColumn>
  );
};

SocialColumn.displayName = 'SocialColumn';
ConnectColumn.displayName = 'ConnectColumn';
FooterColumn.displayName = 'FooterColumn';

FooterColumn.propTypes = {
  links: PropTypes.arrayOf(PropTypes.object),
  businessHours: PropTypes.node,
  content: PropTypes.node,
  title: PropTypes.string.isRequired,
};

ConnectColumn.propTypes = {
  title: PropTypes.string.isRequired,
  appStoreSectionTitle: PropTypes.string,
  socialIcons: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.shape({ src: PropTypes.string, alt: PropTypes.string }),
      targetUrl: PropTypes.string,
      opensInNewTab: PropTypes.bool,
    }),
  ).isRequired,
  appStoreIcons: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.shape({ src: PropTypes.string, alt: PropTypes.string }),
      targetUrl: PropTypes.string,
      opensInNewTab: PropTypes.bool,
    }),
  ).isRequired,
};
